import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { FilterConfig } from '@openx/types/chipFilters.ts';
import { type ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export interface DealsMoreFiltersMetricRangeFieldProps {
  filterConfig: FilterConfig;
  minValue: string;
  maxValue: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  dataTest?: string;
}

export function DealsMoreFiltersMetricRangeField({
  filterConfig,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  dataTest = 'deals-more-filters-metric-section',
}: DealsMoreFiltersMetricRangeFieldProps) {
  const { t } = useTranslation();
  const currencySymbol = filterConfig.numberFilter?.currencySymbol ?? '';

  const handleMinChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onMinChange(event.target.value);
    },
    [onMinChange]
  );

  const handleMaxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onMaxChange(event.target.value);
    },
    [onMaxChange]
  );

  return (
    <Stack
      direction="column"
      data-test={dataTest}
      sx={{
        gap: 1,
      }}
    >
      <Typography component="label" variant="body1Bold">
        {filterConfig.name}
      </Typography>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <TextField
          type="number"
          placeholder={t('Min')}
          value={minValue}
          onChange={handleMinChange}
          size="small"
          variant="outlined"
          sx={{ flex: 1 }}
          slotProps={{
            input: {
              startAdornment: currencySymbol && <InputAdornment position="start">{currencySymbol}</InputAdornment>,
            },
            htmlInput: { 'data-test': `${dataTest}-min-input` },
          }}
        />
        <Typography component="span" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
        <TextField
          type="number"
          placeholder={t('Max')}
          value={maxValue}
          onChange={handleMaxChange}
          size="small"
          variant="outlined"
          sx={{ flex: 1 }}
          slotProps={{
            input: {
              startAdornment: currencySymbol && <InputAdornment position="start">{currencySymbol}</InputAdornment>,
            },
            htmlInput: { 'data-test': `${dataTest}-max-input` },
          }}
        />
      </Stack>
    </Stack>
  );
}
